﻿import { useMemo, useState } from 'react';
import { Grid, useTheme, alpha, ToggleButtonGroup, ToggleButton } from '@mui/material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';
import {
    TrendingUp as TrendingUpIcon,
    PictureAsPdf as PdfIcon,
    Visibility as ModalIcon,
    ErrorOutlined as FailedLoginIcon,
    Pageview as PlanViewIcon,
    People as ActiveUsersIcon,
    AccessTime as LastLoginIcon,
} from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';
import type { MetricsData } from '@/types';
import { themeConstants } from '@/theme';
import {
    MetricsStatCard,
    MetricsChartCard,
    MetricsSectionHeading,
    makeTooltipStyle,
    PIE_COLORS,
} from './index';

interface Props {
    data: MetricsData;
}

const ViewToggle = ({
    value,
    onChange,
    LL,
}: {
    value: 'daily' | 'weekly';
    onChange: (v: 'daily' | 'weekly') => void;
    LL: ReturnType<typeof useI18nContext>['LL'];
}) => (
    <ToggleButtonGroup
        size="small"
        exclusive
        value={value}
        onChange={(_, v) => v && onChange(v)}
        sx={{ height: 28 }}
    >
        <ToggleButton value="daily" sx={{ px: 1.5, fontSize: 11 }}>
            {LL.metricsPage.charts.viewDaily()}
        </ToggleButton>
        <ToggleButton value="weekly" sx={{ px: 1.5, fontSize: 11 }}>
            {LL.metricsPage.charts.viewWeekly()}
        </ToggleButton>
    </ToggleButtonGroup>
);

/** Sums an array of { day: 'YYYY-MM-DD', ...numbers } by ISO weekday (1=Mon … 7=Sun) */
function aggregateByWeekday<T extends Record<string, number>>(
    perDay: ({ day: string } & T)[],
    keys: (keyof T & string)[],
    dayNames: string[]
): ({ weekday: string } & T)[] {
    const buckets = Array.from({ length: 7 }, (_, i) => {
        const obj: Record<string, number | string> = { weekday: dayNames[i] };
        for (const k of keys) obj[k] = 0;
        return obj;
    });
    for (const row of perDay) {
        const dow = (new Date(row.day).getDay() + 6) % 7; // 0=Mon…6=Sun
        for (const k of keys) (buckets[dow][k] as number) += row[k] as number;
    }
    return buckets as ({ weekday: string } & T)[];
}

export const MetricsActivitySection = ({ data }: Props) => {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const tt = makeTooltipStyle(theme);
    const divider = alpha(theme.palette.divider, 0.5);
    const tickStyle = { fill: theme.palette.text.secondary, fontSize: 12 };
    const isFiltered = data.resourceId !== null;
    const filteredLabel = LL.metricsPage.filteredByUser();

    const [loginsView, setLoginsView] = useState<'daily' | 'weekly'>('daily');
    const [planView, setPlanView] = useState<'daily' | 'weekly'>('daily');

    // Localised weekday names Mon–Sun
    const weekdayNames = useMemo(() => {
        // Use a known Monday (2024-01-01 was a Monday) to generate names
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(2024, 0, 1 + i);
            return d.toLocaleDateString(undefined, { weekday: 'short' });
        });
    }, []);

    const loginsPerDay = useMemo(
        () =>
            data.loginsPerDay.map(({ day, ok, failed }) => ({
                day: new Date(day).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                }),
                ok,
                failed,
            })),
        [data.loginsPerDay]
    );

    const loginsPerWeekday = useMemo(
        () =>
            aggregateByWeekday<{ ok: number; failed: number }>(
                data.loginsPerDay as ({ day: string } & { ok: number; failed: number })[],
                ['ok', 'failed'],
                weekdayNames
            ),
        [data.loginsPerDay, weekdayNames]
    );

    const planViewsPerDay = useMemo(
        () =>
            (data.planViews?.perDay ?? []).map(({ day, count }) => ({
                day: new Date(day).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                }),
                count,
            })),
        [data.planViews]
    );

    const planViewsPerWeekday = useMemo(
        () =>
            aggregateByWeekday<{ count: number }>(
                (data.planViews?.perDay ?? []) as ({ day: string } & { count: number })[],
                ['count'],
                weekdayNames
            ),
        [data.planViews, weekdayNames]
    );

    return (
        <MetricsSectionHeading
            icon={<TrendingUpIcon fontSize="small" />}
            title={LL.metricsPage.sections.activity()}
        >
            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                {/* ── Stat cards ── */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.loginsInRange()}
                        value={data.users.loginsInRange}
                        icon={<TrendingUpIcon />}
                        color="primary.main"
                        gradient={themeConstants.gradients.cardPrimary}
                        border={themeConstants.borders.primary}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    />
                </Grid>
                {/* Failed logins mostly can't be tied to a user (a wrong code names nobody) */}
                {isFiltered ? (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <MetricsStatCard
                            label={LL.metricsPage.stats.daysSinceLogin()}
                            value={data.users.daysSinceLogin ?? '–'}
                            icon={<LastLoginIcon />}
                            color="success.main"
                            gradient={themeConstants.gradients.cardSuccess}
                            border={themeConstants.borders.success}
                            filtered
                            filteredLabel={filteredLabel}
                        />
                    </Grid>
                ) : (
                    <>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <MetricsStatCard
                                label={LL.metricsPage.stats.activeUsers()}
                                value={data.users.activeInRange ?? 0}
                                icon={<ActiveUsersIcon />}
                                color="success.main"
                                gradient={themeConstants.gradients.cardSuccess}
                                border={themeConstants.borders.success}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <MetricsStatCard
                                label={LL.metricsPage.stats.loginsFailed()}
                                value={data.loginsFailed}
                                icon={<FailedLoginIcon />}
                                color="error.main"
                                gradient={`linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.15)} 0%, ${alpha(theme.palette.error.dark, 0.15)} 100%)`}
                                border={`1px solid ${alpha(theme.palette.error.main, 0.3)}`}
                            />
                        </Grid>
                    </>
                )}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.modalOpensTotal()}
                        value={data.modalOpens.reduce((s, m) => s + m.count, 0)}
                        icon={<ModalIcon />}
                        color="secondary.main"
                        gradient={themeConstants.gradients.cardSecondary}
                        border={themeConstants.borders.secondary}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.pdfCreated()}
                        value={data.pdfs.total}
                        icon={<PdfIcon />}
                        color="warning.main"
                        gradient={themeConstants.gradients.cardWarning}
                        border={themeConstants.borders.warning}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.planViewsTotal()}
                        value={data.planViews?.total ?? 0}
                        icon={<PlanViewIcon />}
                        color="info.main"
                        gradient={`linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.15)} 0%, ${alpha(theme.palette.info.dark, 0.15)} 100%)`}
                        border={`1px solid ${alpha(theme.palette.info.main, 0.3)}`}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.planViewsWithHistory()}
                        value={data.planViews?.withHistory ?? 0}
                        icon={<PlanViewIcon />}
                        color="info.main"
                        gradient={`linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.dark, 0.1)} 100%)`}
                        border={`1px solid ${alpha(theme.palette.info.main, 0.2)}`}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.planViewsAvgForecast()}
                        value={data.planViews?.avgForecast ?? 0}
                        icon={<PlanViewIcon />}
                        color="info.main"
                        gradient={`linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.dark, 0.1)} 100%)`}
                        border={`1px solid ${alpha(theme.palette.info.main, 0.2)}`}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    />
                </Grid>

                {/* ── Logins chart — daily line ── */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <MetricsChartCard
                        title={LL.metricsPage.charts.loginsPerDay()}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={loginsPerDay}
                                margin={{ left: 0, right: 16, top: 8, bottom: 0 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                <XAxis dataKey="day" tick={tickStyle} interval="preserveStartEnd" />
                                <YAxis allowDecimals={false} tick={tickStyle} />
                                <Tooltip {...tt} />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="ok"
                                    stroke={theme.palette.primary.main}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 5 }}
                                    name={LL.metricsPage.charts.loginsLabel()}
                                />
                                {!isFiltered && (
                                    <Line
                                        type="monotone"
                                        dataKey="failed"
                                        stroke={theme.palette.error.main}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 5 }}
                                        name={LL.metricsPage.charts.loginsFailedLabel()}
                                    />
                                )}
                            </LineChart>
                        </ResponsiveContainer>
                    </MetricsChartCard>
                </Grid>

                {/* ── Logins by hour of day OR by weekday ── */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <MetricsChartCard
                        title={LL.metricsPage.charts.loginsLabel()}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                        action={<ViewToggle value={loginsView} onChange={setLoginsView} LL={LL} />}
                    >
                        <ResponsiveContainer width="100%" height="100%" key={loginsView}>
                            {loginsView === 'daily' ? (
                                <BarChart data={data.loginsPerHour} margin={{ left: 0, right: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis
                                        dataKey="hour"
                                        tick={{ ...tickStyle, fontSize: 10 }}
                                        tickFormatter={(h) => `${h}h`}
                                    />
                                    <YAxis allowDecimals={false} tick={tickStyle} />
                                    <Tooltip {...tt} labelFormatter={(h) => `${h}:00`} />
                                    <Bar
                                        dataKey="count"
                                        fill={theme.palette.primary.main}
                                        radius={[2, 2, 0, 0]}
                                        name={LL.metricsPage.charts.loginsLabel()}
                                    />
                                </BarChart>
                            ) : (
                                <BarChart
                                    data={loginsPerWeekday}
                                    margin={{ left: 0, right: 8, top: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis
                                        dataKey="weekday"
                                        tick={{ ...tickStyle, fontSize: 10 }}
                                    />
                                    <YAxis allowDecimals={false} tick={tickStyle} />
                                    <Tooltip {...tt} />
                                    <Legend />
                                    <Bar
                                        dataKey="ok"
                                        fill={theme.palette.primary.main}
                                        radius={[3, 3, 0, 0]}
                                        name={LL.metricsPage.charts.loginsLabel()}
                                    />
                                    {!isFiltered && (
                                        <Bar
                                            dataKey="failed"
                                            fill={theme.palette.error.main}
                                            radius={[3, 3, 0, 0]}
                                            name={LL.metricsPage.charts.loginsFailedLabel()}
                                        />
                                    )}
                                </BarChart>
                            )}
                        </ResponsiveContainer>
                    </MetricsChartCard>
                </Grid>

                {(data.planViews?.total ?? 0) > 0 && (
                    <>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <MetricsChartCard
                                title={LL.metricsPage.charts.planViewsPerDay()}
                                filtered={isFiltered}
                                filteredLabel={filteredLabel}
                            >
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart
                                        data={planViewsPerDay}
                                        margin={{ left: 0, right: 16, top: 8, bottom: 0 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                        <XAxis
                                            dataKey="day"
                                            tick={tickStyle}
                                            interval="preserveStartEnd"
                                        />
                                        <YAxis allowDecimals={false} tick={tickStyle} />
                                        <Tooltip {...tt} />
                                        <Line
                                            type="monotone"
                                            dataKey="count"
                                            stroke={theme.palette.info.main}
                                            strokeWidth={2}
                                            dot={false}
                                            activeDot={{ r: 5 }}
                                            name={LL.metricsPage.charts.planViewsLabel()}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </MetricsChartCard>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <MetricsChartCard
                                title={LL.metricsPage.charts.planViews()}
                                filtered={isFiltered}
                                filteredLabel={filteredLabel}
                                action={
                                    <ViewToggle value={planView} onChange={setPlanView} LL={LL} />
                                }
                            >
                                <ResponsiveContainer width="100%" height="100%" key={planView}>
                                    {planView === 'daily' ? (
                                        <BarChart
                                            data={data.planViews?.perHour ?? []}
                                            margin={{ left: 0, right: 8 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                            <XAxis
                                                dataKey="hour"
                                                tick={{ ...tickStyle, fontSize: 10 }}
                                                tickFormatter={(h) => `${h}h`}
                                            />
                                            <YAxis allowDecimals={false} tick={tickStyle} />
                                            <Tooltip {...tt} labelFormatter={(h) => `${h}:00`} />
                                            <Bar
                                                dataKey="count"
                                                fill={theme.palette.info.main}
                                                radius={[2, 2, 0, 0]}
                                                name={LL.metricsPage.charts.planViewsLabel()}
                                            />
                                        </BarChart>
                                    ) : (
                                        <BarChart
                                            data={planViewsPerWeekday}
                                            margin={{ left: 0, right: 8, top: 8 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                            <XAxis
                                                dataKey="weekday"
                                                tick={{ ...tickStyle, fontSize: 10 }}
                                            />
                                            <YAxis allowDecimals={false} tick={tickStyle} />
                                            <Tooltip {...tt} />
                                            <Bar
                                                dataKey="count"
                                                fill={theme.palette.info.main}
                                                radius={[3, 3, 0, 0]}
                                                name={LL.metricsPage.charts.planViewsLabel()}
                                            />
                                        </BarChart>
                                    )}
                                </ResponsiveContainer>
                            </MetricsChartCard>
                        </Grid>
                    </>
                )}

                {/* ── Modal opens ── */}
                {data.modalOpens.length > 0 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.modalOpens()}
                            filtered={isFiltered}
                            filteredLabel={filteredLabel}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={data.modalOpens}
                                    layout="vertical"
                                    margin={{ left: 8, right: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis type="number" allowDecimals={false} tick={tickStyle} />
                                    <YAxis
                                        type="category"
                                        dataKey="modal"
                                        tick={{ ...tickStyle, fontSize: 11 }}
                                        width={130}
                                    />
                                    <Tooltip {...tt} />
                                    <Bar
                                        dataKey="count"
                                        fill={theme.palette.secondary.main}
                                        radius={[0, 4, 4, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}

                {/* ── PDF options ── */}
                {data.pdfs.options.length > 0 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.pdfOptions()}
                            filtered={isFiltered}
                            filteredLabel={filteredLabel}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={data.pdfs.options}
                                    layout="vertical"
                                    margin={{ left: 8, right: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis type="number" allowDecimals={false} tick={tickStyle} />
                                    <YAxis
                                        type="category"
                                        dataKey="option"
                                        tick={{ ...tickStyle, fontSize: 11 }}
                                        width={130}
                                    />
                                    <Tooltip {...tt} />
                                    <Bar
                                        dataKey="count"
                                        fill={theme.palette.warning.main}
                                        radius={[0, 4, 4, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}

                {/* ── Failed login breakdown ── */}
                {!isFiltered && data.loginsFailed > 0 && (
                    <>
                        {data.loginsFailedDetail.byReason.length > 0 && (
                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                <MetricsChartCard
                                    title={LL.metricsPage.charts.failedByReason()}
                                    height={200}
                                    filtered={isFiltered}
                                    filteredLabel={filteredLabel}
                                >
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={data.loginsFailedDetail.byReason}
                                            layout="vertical"
                                            margin={{ left: 8, right: 20 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                            <XAxis
                                                type="number"
                                                allowDecimals={false}
                                                tick={tickStyle}
                                            />
                                            <YAxis
                                                type="category"
                                                dataKey="reason"
                                                tick={{ ...tickStyle, fontSize: 10 }}
                                                width={160}
                                            />
                                            <Tooltip {...tt} />
                                            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                                                {data.loginsFailedDetail.byReason.map((_, i) => (
                                                    <Cell
                                                        key={i}
                                                        fill={PIE_COLORS[i % PIE_COLORS.length]}
                                                    />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </MetricsChartCard>
                            </Grid>
                        )}
                        {data.loginsFailedDetail.topCodes.length > 0 && (
                            <Grid size={{ xs: 12, md: 6 }}>
                                <MetricsChartCard
                                    title={LL.metricsPage.charts.failedTopCodes()}
                                    height={200}
                                    filtered={isFiltered}
                                    filteredLabel={filteredLabel}
                                >
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={data.loginsFailedDetail.topCodes}
                                            margin={{ left: 0, right: 16, top: 8 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                            <XAxis
                                                dataKey="code"
                                                tick={{ ...tickStyle, fontFamily: 'monospace' }}
                                            />
                                            <YAxis allowDecimals={false} tick={tickStyle} />
                                            <Tooltip {...tt} />
                                            <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                                                {data.loginsFailedDetail.topCodes.map((_, i) => (
                                                    <Cell
                                                        key={i}
                                                        fill={alpha(
                                                            theme.palette.error.main,
                                                            0.6 +
                                                                0.4 *
                                                                    (1 -
                                                                        i /
                                                                            Math.max(
                                                                                data
                                                                                    .loginsFailedDetail
                                                                                    .topCodes
                                                                                    .length - 1,
                                                                                1
                                                                            ))
                                                        )}
                                                    />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </MetricsChartCard>
                            </Grid>
                        )}
                    </>
                )}
            </Grid>
        </MetricsSectionHeading>
    );
};
